import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiAlertTriangle, FiArrowLeft, FiAward, FiBriefcase, FiCheckCircle, FiFileText, FiMessageCircle, FiMic, FiPhoneOff, FiPlay, FiRefreshCw, FiSquare, FiZap } from 'react-icons/fi';
import { ref } from 'firebase/database';
import { createLocalAudioTrack, Room, RoomEvent, Track } from 'livekit-client';
import { db } from '../../firebaseConfig';
import {
  cancelMockInterviewSession,
  createMockInterviewReport,
  createMockInterviewSession,
  getMockInterviewReport,
  listMockInterviewReports,
  retryMockInterviewReport,
} from '../mockInterviewApi';
import { highMatchInterviewOpportunities, interviewTurnBudget } from '../mockInterviewOpportunities';
import { candidateTurnTransition, realtimePlaybackTransition } from '../mockInterviewRealtime';
import { getCachedFirebaseValue, setCachedDataValue, subscribeCachedValue } from '../firebaseDataCache';
import { useLanguage } from '../contexts/LanguageContext';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { hasPaidAccess } from '../entitlements';
import UpgradeGate from '../components/UpgradeGate';
import { PlanControlSlot } from '../upgrade/UpgradeExperience';
import { readUpgradeDraft, saveUpgradeDraft, clearUpgradeDraft, rememberUpgradeIntent } from '../upgrade/upgradeIntent';
import useMobileLayout from '../components/useMobileLayout';

const INTERVIEW_TYPES = [
  { key: 'structured', label: 'Role-specific interview', detail: 'CV evidence, job-critical competencies, realistic scenarios, and structured scoring.' },
  { key: 'behavioral', label: 'Behavioral', detail: 'STAR stories, judgment, conflict, ownership.' },
  { key: 'technical', label: 'Technical', detail: 'Role-specific skills, tools, scenarios, tradeoffs.' },
  { key: 'recruiter', label: 'Recruiter Screen', detail: 'Motivation, fit, constraints, communication.' },
  { key: 'case', label: 'Case Interview', detail: 'Structure, assumptions, prioritization, business judgment.' },
];

const REPORT_SECTION_LINKS = [
  { id: 'mock-scorecard', label: 'Scorecard' },
  { id: 'mock-answers', label: 'Answer coaching' },
  { id: 'mock-communication', label: 'Communication' },
  { id: 'mock-alignment', label: 'Role fit' },
  { id: 'mock-practice', label: 'Practice plan' },
];

const DEFAULT_MAX_INTERVIEW_SECONDS = 20 * 60;
const MAX_INTERVIEW_QUESTION_TURNS = 6;
const STARTUP_TIMEOUT_MS = 30000;
const CANDIDATE_TURN_GRACE_MS = 700;
const REALTIME_SDP_URL = 'https://api.openai.com/v1/realtime/calls';
function debugLog(event, payload = {}) {
  // Intentionally visible in DevTools while this beta voice flow is stabilizing.
  console.info('[mock-interview]', event, payload);
}

function debugError(event, payload = {}) {
  console.error('[mock-interview]', event, payload);
}

function secondsToClock(value) {
  const minutes = Math.floor(value / 60).toString().padStart(2, '0');
  const seconds = Math.floor(value % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function interviewTypeFor(key) {
  return INTERVIEW_TYPES.find((item) => item.key === key) || INTERVIEW_TYPES[0];
}

function formatReportDate(value, language = 'en', fallback = 'Recent') {
  if (!value) return fallback;
  try {
    return new Intl.DateTimeFormat(language === 'ar' ? 'ar-LB' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value));
  } catch {
    return fallback;
  }
}

function scoreLabel(score, fallback = 'No score') {
  if (typeof score !== 'number' || Number.isNaN(score)) return fallback;
  return `${Math.max(0, Math.min(100, Math.round(score)))}/100`;
}

function scoreTone(score) {
  if (typeof score !== 'number' || Number.isNaN(score)) return 'neutral';
  if (score >= 80) return 'strong';
  if (score >= 65) return 'steady';
  return 'risk';
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function cleanItems(value) {
  return asArray(value).map((item) => String(item || '').trim()).filter(Boolean);
}

function reportScore(report) {
  const value = report?.overall?.score ?? report?.score;
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function reportTitle(record, fallback = 'Interview coaching report') {
  return record?.report?.overall?.title || record?.report?.title || fallback;
}

function reportSummary(record) {
  return record?.report?.overall?.summary || record?.report?.summary || '';
}

function reportGenerationFailed(record) {
  return record?.reportGeneration?.status === 'failed' || record?.report?.generationStatus === 'failed';
}

function reportStatusCopy(record, translate) {
  if (reportGenerationFailed(record)) return translate('failedStatus');
  if (record?.truncated) return translate('truncated');
  return translate('completed');
}

function buildDemoPrompts(sessionPayload, selectedType) {
  if (Array.isArray(sessionPayload?.demoPrompts) && sessionPayload.demoPrompts.length > 0) {
    return sessionPayload.demoPrompts;
  }
  const opening = sessionPayload?.openingPrompt
    || `We will run this as a ${selectedType.label} interview. First tell me what role or field you want to practice for.`;
  return [
    opening,
    'What responsibilities, skills, or situations should this practice interview focus on for that role?',
    'Tell me about a relevant project, responsibility, or achievement from your background.',
    'Final question. What would make you feel ready for a real interview in this field?',
  ];
}

function MockInterviewScreen({ user, profile, reportsOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const { entitlements } = useEntitlements();
  const paid = hasPaidAccess(entitlements);
  const tm = (key) => t(`mockInterviewPage.${key}`);
  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
  const operational = (key) => tm(`operational.${key}`);
  const localizedRuntimeError = (error, fallbackKey) => {
    const message = String(error?.message || '');
    if (language === 'ar' && !/[\u0600-\u06ff]/.test(message)) return operational(fallbackKey);
    return message || operational(fallbackKey);
  };
  const interviewTypes = INTERVIEW_TYPES.map((item) => ({ key: item.key, label: tm(`types.${item.key}`)[0], detail: tm(`types.${item.key}`)[1] }));
  const reportLinks = REPORT_SECTION_LINKS.map((link) => ({ ...link, label: tm(`sections.${link.id.replace('mock-', '') === 'alignment' ? 'alignment' : link.id.replace('mock-', '')}`) }));
  const { reportId } = useParams();
  const reportsCacheKey = user?.uid ? `api/${user.uid}/mock-interview/reports` : '';
  const reportDetailCacheKey = user?.uid && reportId
    ? `api/${user.uid}/mock-interview/reports/${reportId}`
    : '';
  const cachedReports = reportsCacheKey ? getCachedFirebaseValue(reportsCacheKey) : undefined;
  const cachedReportDetail = reportDetailCacheKey ? getCachedFirebaseValue(reportDetailCacheKey) : undefined;
  const opportunitiesPath = user?.uid ? `people/${user.uid}/jobScoutFeeds` : '';
  const cachedOpportunities = opportunitiesPath ? getCachedFirebaseValue(opportunitiesPath) : undefined;
  const [interviewType] = useState('structured');
  const [opportunities, setOpportunities] = useState(() => highMatchInterviewOpportunities(cachedOpportunities || {}));
  const mobile = useMobileLayout();
  const [jobQuery, setJobQuery] = useState('');
  const visibleOpportunities = !mobile || !jobQuery.trim() ? opportunities : opportunities.filter(opportunity =>
    `${opportunity.title} ${opportunity.companyName} ${opportunity.location}`.toLocaleLowerCase(language).includes(jobQuery.trim().toLocaleLowerCase(language)));
  const [opportunitiesLoaded, setOpportunitiesLoaded] = useState(!user?.uid || cachedOpportunities !== undefined);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState(() => location.state?.jobId || readUpgradeDraft(user?.uid, 'interview')?.jobId || '');
  const [status, setStatus] = useState(reportsOnly && reportId ? 'report' : reportsOnly ? 'reports' : 'setup');
  const [mode, setMode] = useState('idle');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [, setTranscript] = useState([]);
  const [reportRecord, setReportRecord] = useState(cachedReportDetail || null);
  const [previousReports, setPreviousReports] = useState(cachedReports || []);
  const [reportsLoading, setReportsLoading] = useState(Boolean(user) && cachedReports === undefined);
  const [reportDetailLoading, setReportDetailLoading] = useState(Boolean(reportsOnly && reportId && cachedReportDetail === undefined));
  const [reportRetrying, setReportRetrying] = useState(false);
  const [activeInterviewType, setActiveInterviewType] = useState(null);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [, setInterviewerQuestionCount] = useState(0);
  const [ending, setEnding] = useState(false);
  const [, setPreparingMessage] = useState('');
  const [, setStartupSteps] = useState([]);
  const [activeReportSection, setActiveReportSection] = useState(REPORT_SECTION_LINKS[0].id);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const liveKitRoomRef = useRef(null);
  const liveKitMicrophoneTrackRef = useRef(null);
  const liveKitOpeningCompleteRef = useRef(false);
  const sessionInfoRef = useRef(null);
  const startedAtRef = useRef(null);
  const elapsedRef = useRef(0);
  const startPendingRef = useRef(false);
  const transcriptRef = useRef([]);
  const activeInterviewTypeRef = useRef('structured');
  const openingRequestedRef = useRef(false);
  const dataChannelOpenRef = useRef(false);
  const remoteDescriptionReadyRef = useRef(false);
  const agentEndingRef = useRef(false);
  const endingRef = useRef(false);
  const openingInProgressRef = useRef(false);
  const assistantResponseInProgressRef = useRef(false);
  const audioPlaybackActiveRef = useRef(false);
  const responseDoneReceivedRef = useRef(false);
  const responseDoneTranscriptRef = useRef('');
  const responseCompletionStatusRef = useRef('completed');
  const openingRetryCountRef = useRef(0);
  const candidateHasSpokenRef = useRef(false);
  const candidateTurnTimerRef = useRef(null);
  const interviewerQuestionCountRef = useRef(0);
  const pendingAgentEndRef = useRef(null);
  const agentEndTimerRef = useRef(null);
  const functionCallsRef = useRef({});

  const displayedType = interviewTypes.find((item) => item.key === (reportRecord?.interviewType || activeInterviewType || interviewType)) || interviewTypes[0];
  const selectedOpportunity = useMemo(
    () => opportunities.find((item) => item.id === selectedOpportunityId) || null,
    [opportunities, selectedOpportunityId],
  );
  const hasUploadedCv = Boolean(
    profile?.cvMetadata?.storagePath
    || profile?.cvStoragePath
    || profile?.cv?.Link
    || profile?.jobScouts?.[profile?.primaryScoutId]?.cv?.storagePath
  );
  const screenStatus = reportsOnly ? (reportId ? 'report' : 'reports') : status === 'reports' ? 'setup' : status;
  const isInitializingInterview = screenStatus === 'connecting';
  const localizedStatus = language === 'ar'
    ? ({ setup: 'الإعداد', connecting: 'جارٍ الاتصال', live: 'مباشر', reporting: 'جارٍ إنشاء التقرير', report: 'التقرير', reports: 'التقارير', voice: 'صوتي', demo: 'تجريبي' }[screenStatus === 'live' ? mode : screenStatus] || screenStatus)
    : (screenStatus === 'live' ? mode : screenStatus);

  const recordStartupStep = (label, payload = {}) => {
    const step = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      label,
      payload,
    };
    setStartupSteps((items) => [...items.slice(-7), step]);
    debugLog('startup_step', { label, ...payload });
  };

  useEffect(() => {
    if (status !== 'live') return undefined;
    const id = window.setInterval(() => {
      if (startedAtRef.current) {
        const nextElapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
        elapsedRef.current = nextElapsed;
        setElapsed(nextElapsed);
        const maxSeconds = sessionInfo?.maxDurationSeconds || DEFAULT_MAX_INTERVIEW_SECONDS;
        if (nextElapsed >= maxSeconds && !ending) {
          endInterview(false);
        }
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [status, ending]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || !paid) {
      setPreviousReports([]);
      setReportsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const hasCachedReports = getCachedFirebaseValue(reportsCacheKey) !== undefined;
    setReportsLoading(!hasCachedReports);
    listMockInterviewReports({ user, limit: 10 })
      .then((payload) => {
        if (!cancelled) {
          const reports = Array.isArray(payload?.reports) ? payload.reports : [];
          setCachedDataValue(reportsCacheKey, reports);
          setPreviousReports(reports);
        }
      })
      .catch((err) => {
        debugError('report_history_load_failed', { message: err?.message || 'unknown error' });
      })
      .finally(() => {
        if (!cancelled) setReportsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [paid, user, reportsCacheKey]);

  useEffect(() => {
    if (!user?.uid) {
      setOpportunities([]);
      setOpportunitiesLoaded(true);
      return undefined;
    }
    const feedRef = ref(db, opportunitiesPath);
    const handler = (snapshot) => {
      const nextOpportunities = highMatchInterviewOpportunities(snapshot.val() || {});
      setOpportunities(nextOpportunities);
      setSelectedOpportunityId((current) => (
        nextOpportunities.some((item) => item.id === current) ? current : ''
      ));
      setOpportunitiesLoaded(true);
    };
    return subscribeCachedValue(feedRef, opportunitiesPath, handler);
  }, [user?.uid, opportunitiesPath]);

  useEffect(() => () => cleanupRealtime(), []);

  useEffect(() => {
    if (reportsOnly) {
      if (reportId) {
        if (status !== 'report') setStatus('report');
        return;
      }
      if (status === 'setup' || status === 'report') {
        setStatus('reports');
        setReportRecord(null);
      }
      return;
    }
    if (status === 'reports') {
      setStatus('setup');
      setReportRecord(null);
    }
  }, [reportsOnly, reportId, status]);

  useEffect(() => {
    if (!reportsOnly || !reportId || !user) return undefined;
    let cancelled = false;
    setStatus('report');
    setError('');
    const cachedDetail = getCachedFirebaseValue(reportDetailCacheKey) || null;
    setReportRecord((current) => (current?.id === reportId ? current : cachedDetail));
    setReportDetailLoading(!cachedDetail);
    getMockInterviewReport({ user, reportId })
      .then((detail) => {
        if (cancelled) return;
        setCachedDataValue(reportDetailCacheKey, detail);
        setReportRecord(detail);
        const typeKey = detail?.interviewType || interviewType;
        setActiveInterviewType(typeKey);
        activeInterviewTypeRef.current = typeKey;
      })
      .catch((err) => {
        if (!cancelled) setError(localizedRuntimeError(err, 'loadReport'));
      })
      .finally(() => {
        if (!cancelled) setReportDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportsOnly, reportId, user, reportDetailCacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!reportsOnly || reportId || !reportRecord?.id) return;
    if (status === 'report') {
      navigate(`/mock-interview/reports/${encodeURIComponent(reportRecord.id)}`, { replace: true });
      return;
    }
  }, [reportsOnly, reportId, reportRecord?.id, status, navigate]);

  useEffect(() => {
  }, [status, reportRecord?.id]);

  useEffect(() => {
    if (status !== 'report' || reportGenerationFailed(reportRecord)) return undefined;

    const updateActiveSection = () => {
      const offset = 150;
      const visible = REPORT_SECTION_LINKS.reduce((current, link) => {
        const element = document.getElementById(link.id);
        if (!element) return current;
        return element.getBoundingClientRect().top <= offset ? link.id : current;
      }, REPORT_SECTION_LINKS[0].id);
      setActiveReportSection(visible);
    };

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);
    return () => {
      window.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [status, reportRecord]);

  const cleanupRealtime = () => {
    window.clearTimeout(agentEndTimerRef.current);
    window.clearTimeout(candidateTurnTimerRef.current);
    try {
      dataChannelRef.current?.close?.();
    } catch {
      // Ignore data-channel shutdown races during navigation/reporting.
    }
    try {
      peerConnectionRef.current?.getSenders?.().forEach((sender) => sender.track?.stop?.());
      peerConnectionRef.current?.close?.();
    } catch {
      // Ignore peer-connection shutdown races during navigation/reporting.
    }
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    try {
      liveKitRoomRef.current?.unregisterTextStreamHandler?.('lk.transcription');
      liveKitRoomRef.current?.disconnect?.();
    } catch {
      // Ignore room shutdown races while the report screen is opening.
    }
    liveKitMicrophoneTrackRef.current?.stop?.();
    liveKitMicrophoneTrackRef.current = null;
    liveKitRoomRef.current = null;
    liveKitOpeningCompleteRef.current = false;
    peerConnectionRef.current = null;
    dataChannelRef.current = null;
    streamRef.current = null;
    dataChannelOpenRef.current = false;
    remoteDescriptionReadyRef.current = false;
    functionCallsRef.current = {};
    openingInProgressRef.current = false;
    assistantResponseInProgressRef.current = false;
    audioPlaybackActiveRef.current = false;
    responseDoneReceivedRef.current = false;
    responseDoneTranscriptRef.current = '';
    responseCompletionStatusRef.current = 'completed';
    openingRetryCountRef.current = 0;
    candidateHasSpokenRef.current = false;
    candidateTurnTimerRef.current = null;
    pendingAgentEndRef.current = null;
    agentEndTimerRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause?.();
      audioRef.current.srcObject = null;
    }
    audioRef.current = null;
  };

  const addTranscript = (role, text) => {
    const clean = String(text || '').trim();
    if (!clean) return;
    const last = transcriptRef.current[transcriptRef.current.length - 1];
    if (last?.role === role && last?.text === clean) return;
    transcriptRef.current = [...transcriptRef.current, {
      role,
      text: clean,
      timestamp: Date.now(),
      elapsedSeconds: elapsedRef.current || 0,
    }];
    setTranscript(transcriptRef.current);
  };

  const markLiveStarted = () => {
    if (!startedAtRef.current) {
      startedAtRef.current = Date.now();
      elapsedRef.current = 0;
      setElapsed(0);
      recordStartupStep('Interviewer first response started.');
    }
    setStatus('live');
  };

  const sendRealtimeEvent = (event) => {
    const channel = dataChannelRef.current;
    if (!channel || channel.readyState !== 'open') return false;
    channel.send(JSON.stringify(event));
    debugLog('realtime_event_sent', { type: event.type });
    return true;
  };

  const scrollToReportSection = (event, sectionId) => {
    event.preventDefault();
    const element = document.getElementById(sectionId);
    if (!element) return;
    setActiveReportSection(sectionId);
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setMicrophoneEnabled = (enabled) => {
    if (liveKitRoomRef.current) {
      liveKitRoomRef.current.localParticipant
        .setMicrophoneEnabled(enabled)
        .catch((err) => debugError('livekit_microphone_state_failed', { message: err?.message }));
      return;
    }
    streamRef.current?.getAudioTracks?.().forEach((track) => {
      track.enabled = enabled;
    });
    debugLog('microphone_track_state', { enabled });
  };

  const setCandidateSpeechState = (hasSpoken) => {
    candidateHasSpokenRef.current = hasSpoken;
  };

  const requestInterviewerResponse = (trigger = 'automatic_turn_detection') => {
    window.clearTimeout(candidateTurnTimerRef.current);
    candidateTurnTimerRef.current = null;
    if (
      !candidateHasSpokenRef.current
      || assistantResponseInProgressRef.current
      || endingRef.current
      || agentEndingRef.current
    ) return;
    setMicrophoneEnabled(false);
    setCandidateSpeechState(false);
    setMode('thinking');
    const {
      elapsedSeconds,
      remainingSeconds,
      questionsUsed,
      questionsRemaining,
      mustClose,
      followUpRule,
    } = interviewTurnBudget({
      elapsedSeconds: elapsedRef.current,
      maxDurationSeconds: sessionInfoRef.current?.maxDurationSeconds || DEFAULT_MAX_INTERVIEW_SECONDS,
      questionsUsed: interviewerQuestionCountRef.current,
      maxQuestionTurns: MAX_INTERVIEW_QUESTION_TURNS,
    });
    window.setTimeout(() => {
      const sent = sendRealtimeEvent({
        type: 'response.create',
        response: {
          instructions: [
            'The candidate finished a complete speaking turn and the end-of-turn detector waited for continuation.',
            `Live interview clock: ${secondsToClock(elapsedSeconds)} elapsed, ${secondsToClock(remainingSeconds)} remaining.`,
            `Question budget: ${questionsUsed} of ${MAX_INTERVIEW_QUESTION_TURNS} interviewer question turns used; ${questionsRemaining} remaining.`,
            'Respond as the interviewer now; never respond to silence or an earlier audio fragment.',
            'Do not repeat the opening or greeting.',
            mustClose
              ? 'The question or time budget is exhausted. Do not ask another question. Give one concise, natural closing sentence and call the end_mock_interview tool now.'
              : `Time-aware follow-up rule: ${followUpRule}`,
            !mustClose
              ? 'React to the substance of the answer like an experienced interviewer. If the time-aware rule permits a follow-up, make it specific to personal contribution, reasoning, constraint, tradeoff, stakeholder, scale, result, or lesson.'
              : '',
            !mustClose
              ? 'Otherwise use a natural transition and ask the next concise, job-related question in the prepared competency plan.'
              : '',
            'Do not praise every answer, read a rubric, coach the candidate, or reveal an ideal answer during the interview.',
            'Use only the selected job description and truthful CV evidence.',
          ].filter(Boolean).join(' '),
          output_modalities: ['audio'],
          metadata: {
            purpose: mustClose ? 'mock_interview_budget_close' : 'mock_interview_candidate_turn_complete',
            elapsedSeconds,
            remainingSeconds,
            questionsUsed,
            questionsRemaining,
          },
        },
      });
      if (!sent) {
        setError(operational('connectionNotReady'));
        setMicrophoneEnabled(true);
        setCandidateSpeechState(true);
        setMode('listening');
        return;
      }
      if (!mustClose) {
        const nextQuestionCount = questionsUsed + 1;
        interviewerQuestionCountRef.current = nextQuestionCount;
        setInterviewerQuestionCount(nextQuestionCount);
      }
      recordStartupStep('Candidate turn ended naturally. Interviewer response requested.', { trigger });
    }, 250);
  };

  const scheduleInterviewerResponse = () => {
    window.clearTimeout(candidateTurnTimerRef.current);
    candidateTurnTimerRef.current = window.setTimeout(() => {
      candidateTurnTimerRef.current = null;
      requestInterviewerResponse();
    }, CANDIDATE_TURN_GRACE_MS);
  };

  const estimateAudioDrainMs = (text) => {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean).length;
    if (!words) return 2200;
    return Math.max(2200, Math.min(7000, words * 260));
  };

  const requestOpeningResponse = (sessionPayload, trigger = 'unknown') => {
    if (openingRequestedRef.current) return;
    if (!dataChannelOpenRef.current || !remoteDescriptionReadyRef.current) {
      debugLog('opening_deferred', {
        trigger,
        dataChannelOpen: dataChannelOpenRef.current,
        remoteDescriptionReady: remoteDescriptionReadyRef.current,
      });
      return;
    }
    openingRequestedRef.current = true;
    const typeMeta = interviewTypeFor(sessionPayload.interviewType || activeInterviewTypeRef.current || interviewType);
    const opening = sessionPayload.openingPrompt
      || `Hi, welcome in! Today we're going to practice a 20-minute ${typeMeta.label} mock interview. Let's start with question one: what role or field should we practice for today?`;
    const sent = sendRealtimeEvent({
      type: 'response.create',
      response: {
        instructions: [
          'This is the one-time opening turn for the mock interview.',
          'Read the complete opening script below as one natural spoken turn.',
          'Say every sentence. Do not shorten, paraphrase, summarize, or stop early.',
          'Finish the greeting and setup before asking question one.',
          'Finish the entire first question before stopping.',
          'After question one, stop and listen. Do not repeat this opening or restart the session on later turns.',
          '',
          'Do not say the script labels.',
          '<opening_script>',
          opening,
          '</opening_script>',
        ].join('\n'),
        input: [],
        output_modalities: ['audio'],
        metadata: { purpose: 'mock_interview_opening' },
      },
    });
    if (!sent) {
      openingRequestedRef.current = false;
      return;
    }
    openingInProgressRef.current = true;
    interviewerQuestionCountRef.current = 1;
    setInterviewerQuestionCount(1);
    addTranscript('interviewer', opening);
    recordStartupStep('Opening interviewer response requested.', { trigger });
  };

  const transcriptFromResponseDone = (event) => {
    const output = event?.response?.output;
    if (!Array.isArray(output)) return '';
    const parts = [];
    output.forEach((item) => {
      (item.content || []).forEach((content) => {
        if (content.transcript) parts.push(content.transcript);
        if (content.text) parts.push(content.text);
      });
    });
    return parts.join(' ').trim();
  };

  const transcriptFromItem = (item) => {
    if (!item || !Array.isArray(item.content)) return '';
    const parts = [];
    item.content.forEach((content) => {
      if (content.transcript) parts.push(content.transcript);
      if (content.text) parts.push(content.text);
    });
    return parts.join(' ').trim();
  };

  const roleFromItem = (item) => {
    if (item?.role === 'assistant') return 'interviewer';
    if (item?.role === 'user') return 'candidate';
    return '';
  };

  const parseToolArguments = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  };

  const queueEndFromAgentTool = (toolCall) => {
    if (!toolCall || toolCall.name !== 'end_mock_interview' || agentEndingRef.current) return false;
    const args = parseToolArguments(toolCall.arguments);
    const reason = args.reason || args.completionSummary || 'The interviewer completed the mock interview.';
    pendingAgentEndRef.current = { reason };
    recordStartupStep('Interviewer requested report generation after final message.', { reason });
    return true;
  };

  const completePendingAgentEnd = (lastAssistantText = '') => {
    if (!pendingAgentEndRef.current || agentEndingRef.current) return false;
    const pending = pendingAgentEndRef.current;
    agentEndingRef.current = true;
    const delayMs = estimateAudioDrainMs(lastAssistantText);
    recordStartupStep('Waiting for final interviewer audio to finish before report generation.', {
      delayMs,
      reason: pending.reason,
    });
    window.clearTimeout(agentEndTimerRef.current);
    agentEndTimerRef.current = window.setTimeout(() => {
      endInterview(false, transcriptRef.current, {
        endedByAgent: true,
        completionReason: pending.reason,
      });
    }, delayMs);
    return true;
  };

  const finishInterviewerTurn = (lastAssistantText = '', sessionPayload = null) => {
    const responseStatus = responseCompletionStatusRef.current;
    assistantResponseInProgressRef.current = false;
    audioPlaybackActiveRef.current = false;
    responseDoneReceivedRef.current = false;
    responseDoneTranscriptRef.current = '';
    responseCompletionStatusRef.current = 'completed';
    if (
      openingInProgressRef.current
      && responseStatus !== 'completed'
      && openingRetryCountRef.current < 1
      && sessionPayload
    ) {
      openingInProgressRef.current = false;
      openingRequestedRef.current = false;
      openingRetryCountRef.current += 1;
      setMicrophoneEnabled(false);
      setMode('thinking');
      recordStartupStep('Opening audio was incomplete. Retrying the full greeting and first question.', {
        responseStatus,
      });
      window.setTimeout(() => requestOpeningResponse(sessionPayload, 'opening.retry'), 250);
      return;
    }
    if (openingInProgressRef.current) {
      openingInProgressRef.current = false;
      recordStartupStep('Opening audio finished. Microphone enabled for candidate response.');
    }
    if (completePendingAgentEnd(lastAssistantText)) return;
    markLiveStarted();
    setCandidateSpeechState(false);
    setMicrophoneEnabled(true);
    setMode('listening');
  };

  const handleFunctionCallEvent = (event) => {
    const item = event.item || event.output_item || {};
    const itemId = item.id || event.item_id || event.call_id;
    if (event.type === 'response.output_item.added' && item.type === 'function_call' && itemId) {
      functionCallsRef.current[itemId] = {
        name: item.name,
        arguments: item.arguments || '',
      };
      return true;
    }
    if (event.type === 'response.function_call_arguments.delta' && itemId) {
      const current = functionCallsRef.current[itemId] || { name: event.name, arguments: '' };
      current.arguments = `${current.arguments || ''}${event.delta || ''}`;
      functionCallsRef.current[itemId] = current;
      return true;
    }
    if (event.type === 'response.function_call_arguments.done') {
      const current = functionCallsRef.current[itemId] || {};
      return queueEndFromAgentTool({
        name: event.name || current.name,
        arguments: event.arguments || current.arguments,
      });
    }
    return false;
  };

  const handleResponseFunctionCalls = (event) => {
    const output = event?.response?.output;
    if (!Array.isArray(output)) return false;
    return output.some((item) => (
      item?.type === 'function_call'
      && queueEndFromAgentTool({ name: item.name, arguments: item.arguments })
    ));
  };

  const handleRealtimeEvent = (event, sessionPayload, resolveOpening) => {
    debugLog('realtime_event_received', { type: event.type });
    if (event.type === 'session.created') {
      recordStartupStep('Realtime session created.');
      requestOpeningResponse(sessionPayload, 'session.created');
      return;
    }
    if (handleFunctionCallEvent(event)) {
      return;
    }
    if (event.type === 'input_audio_buffer.speech_started') {
      const transition = candidateTurnTransition(event.type);
      if (transition.candidateTurnObserved) setCandidateSpeechState(true);
      if (transition.cancelPendingResponse) {
        window.clearTimeout(candidateTurnTimerRef.current);
        candidateTurnTimerRef.current = null;
      }
      if (startedAtRef.current && !assistantResponseInProgressRef.current) {
        setCandidateSpeechState(true);
        setMode('listening');
      }
      return;
    }
    if (event.type === 'input_audio_buffer.speech_stopped') {
      const transition = candidateTurnTransition(event.type);
      if (transition.candidateTurnObserved) setCandidateSpeechState(true);
      if (
        transition.scheduleResponse
        && startedAtRef.current
        && !assistantResponseInProgressRef.current
      ) {
        setMode('thinking');
        scheduleInterviewerResponse();
      }
      return;
    }
    if (event.type === 'conversation.item.input_audio_transcription.delta') {
      const transition = candidateTurnTransition(event.type);
      if (transition.candidateTurnObserved) setCandidateSpeechState(true);
      if (startedAtRef.current && !assistantResponseInProgressRef.current) setMode('listening');
      return;
    }
    if (event.type === 'input_audio_buffer.committed') {
      const transition = candidateTurnTransition(event.type);
      if (
        transition.candidateTurnObserved
        && startedAtRef.current
        && !assistantResponseInProgressRef.current
      ) {
        setCandidateSpeechState(true);
        setMode('thinking');
        scheduleInterviewerResponse();
      }
      return;
    }
    if (event.type === 'conversation.item.input_audio_transcription.completed') {
      addTranscript('candidate', event.transcript);
      const transition = candidateTurnTransition(event.type);
      if (startedAtRef.current && !assistantResponseInProgressRef.current) {
        if (transition.candidateTurnObserved) setCandidateSpeechState(true);
        setMode('thinking');
        if (transition.scheduleResponse) scheduleInterviewerResponse();
      }
      return;
    }
    if (event.type === 'conversation.item.input_audio_transcription.failed') {
      debugError('input_transcription_failed', event.error || {});
      recordStartupStep('Candidate speech transcription failed.', { type: 'transcription_failed' });
      return;
    }
    if (event.type === 'conversation.item.done') {
      const role = roleFromItem(event.item);
      const text = transcriptFromItem(event.item);
      if (role && text) addTranscript(role, text);
      return;
    }
    if (event.type === 'output_audio_buffer.started') {
      const transition = realtimePlaybackTransition(event.type, audioPlaybackActiveRef.current);
      audioPlaybackActiveRef.current = transition.audioPlaybackActive;
      assistantResponseInProgressRef.current = true;
      setMicrophoneEnabled(false);
      markLiveStarted();
      setMode('speaking');
      resolveOpening?.();
      return;
    }
    if (
      event.type === 'output_audio_buffer.stopped'
      || event.type === 'output_audio_buffer.cleared'
    ) {
      const transition = realtimePlaybackTransition(event.type, audioPlaybackActiveRef.current);
      audioPlaybackActiveRef.current = transition.audioPlaybackActive;
      if (transition.turnFinished && responseDoneReceivedRef.current) {
        finishInterviewerTurn(responseDoneTranscriptRef.current, sessionPayload);
      }
      return;
    }
    if (event.type === 'response.content_part.done') {
      const text = event.part?.transcript || event.part?.text || '';
      if (text) addTranscript('interviewer', text);
      return;
    }
    if (
      event.type === 'response.created'
      || event.type === 'response.output_item.added'
      || event.type === 'response.output_audio.delta'
      || event.type === 'response.audio.delta'
    ) {
      assistantResponseInProgressRef.current = true;
      setMicrophoneEnabled(false);
      markLiveStarted();
      setMode('speaking');
      resolveOpening?.();
      return;
    }
    if (
      event.type === 'response.output_audio_transcript.done'
      || event.type === 'response.audio_transcript.done'
    ) {
      addTranscript('interviewer', event.transcript);
      return;
    }
    if (event.type === 'response.done') {
      const transcript = transcriptFromResponseDone(event);
      if (transcript) addTranscript('interviewer', transcript);
      handleResponseFunctionCalls(event);
      const responseStatus = event.response?.status || 'completed';
      responseCompletionStatusRef.current = responseStatus;
      if (responseStatus !== 'completed') {
        const reason = event.response?.status_details?.reason || responseStatus;
        debugError('interviewer_response_not_completed', { responseStatus, reason });
        if (!openingInProgressRef.current || openingRetryCountRef.current >= 1) {
          setError(operational('audioIncomplete'));
        }
      }
      responseDoneReceivedRef.current = true;
      responseDoneTranscriptRef.current = transcript;
      const transition = realtimePlaybackTransition(event.type, audioPlaybackActiveRef.current);
      audioPlaybackActiveRef.current = transition.audioPlaybackActive;
      if (transition.turnFinished) finishInterviewerTurn(transcript, sessionPayload);
      return;
    }
    if (event.type === 'error') {
      const message = localizedRuntimeError(event.error, 'voiceFailed');
      setError(message);
      recordStartupStep(message, { type: 'error' });
      debugError('realtime_error', event.error || {});
      setMode('idle');
      setStatus((currentStatus) => (currentStatus === 'connecting' ? 'setup' : currentStatus));
    }
  };

  const startRealtimeInterview = async (sessionPayload) => {
    if (!sessionPayload.sessionId) throw new Error(operational('missingSession'));
    if (!sessionPayload.clientSecret) throw new Error(operational('missingRealtimeSecret'));
    openingRequestedRef.current = false;
    dataChannelOpenRef.current = false;
    remoteDescriptionReadyRef.current = false;
    agentEndingRef.current = false;
    audioPlaybackActiveRef.current = false;
    responseDoneReceivedRef.current = false;
    responseDoneTranscriptRef.current = '';
    responseCompletionStatusRef.current = 'completed';
    openingRetryCountRef.current = 0;
    functionCallsRef.current = {};
    debugLog('voice_start_requested', {
      sessionId: sessionPayload.sessionId,
      provider: sessionPayload.provider,
      configured: sessionPayload.configured,
      model: sessionPayload.model,
    });
    recordStartupStep('Requesting microphone permission.');
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    setMicrophoneEnabled(false);
    recordStartupStep('Microphone stream ready.', { tracks: stream.getTracks().length });

    await new Promise(async (resolve, reject) => {
      let opened = false;
      let settled = false;
      const startupTimer = window.setTimeout(() => {
        if (opened) return;
        debugError('voice_startup_timeout', { sessionId: sessionPayload.sessionId });
        cleanupRealtime();
        reject(new Error(operational('startupTimeout')));
      }, STARTUP_TIMEOUT_MS);

      const resolveOpening = () => {
        if (settled) return;
        opened = true;
        settled = true;
        window.clearTimeout(startupTimer);
        resolve();
      };

      const fail = (message) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(startupTimer);
        reject(new Error(message));
      };

      try {
        recordStartupStep('Creating OpenAI Realtime peer connection.');
        const peerConnection = new RTCPeerConnection();
        peerConnectionRef.current = peerConnection;

        const remoteAudio = new Audio();
        remoteAudio.autoplay = true;
        remoteAudio.playsInline = true;
        audioRef.current = remoteAudio;
        peerConnection.ontrack = (event) => {
          const [remoteStream] = event.streams;
          if (!remoteStream) return;
          remoteAudio.srcObject = remoteStream;
          remoteAudio.play?.().catch((err) => {
            setError(operational('audioBlocked'));
            recordStartupStep('Interviewer audio playback was blocked or failed.', { message: err?.message || 'unknown playback error' });
          });
          recordStartupStep('Remote interviewer audio track attached.');
        };
        peerConnection.onconnectionstatechange = () => {
          debugLog('peer_connection_state', { state: peerConnection.connectionState });
          if (['failed', 'disconnected', 'closed'].includes(peerConnection.connectionState) && !opened) {
            fail(operational('connectionFailed'));
          }
        };

        stream.getTracks().forEach((track) => peerConnection.addTrack(track, stream));
        const dataChannel = peerConnection.createDataChannel('oai-events');
        dataChannelRef.current = dataChannel;
        dataChannel.addEventListener('open', () => {
          dataChannelOpenRef.current = true;
          recordStartupStep('Realtime data channel opened.');
          requestOpeningResponse(sessionPayload, 'data_channel.open');
        });
        dataChannel.addEventListener('message', (message) => {
          try {
            handleRealtimeEvent(JSON.parse(message.data), sessionPayload, resolveOpening);
          } catch (err) {
            debugError('realtime_malformed_event', { message: err?.message || 'parse failed' });
          }
        });
        dataChannel.addEventListener('error', () => {
          recordStartupStep('Realtime data channel reported an error.');
        });

        recordStartupStep('Creating Realtime SDP offer.');
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        recordStartupStep('Sending SDP offer to OpenAI Realtime.');
        const sdpResponse = await fetch(REALTIME_SDP_URL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${sessionPayload.clientSecret}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        });
        const answerSdp = await sdpResponse.text();
        if (!sdpResponse.ok) {
          debugError('realtime_sdp_failed', { status: sdpResponse.status, body: answerSdp });
          fail(operational('secureExchangeFailed'));
          return;
        }
        await peerConnection.setRemoteDescription({ type: 'answer', sdp: answerSdp });
        remoteDescriptionReadyRef.current = true;
        recordStartupStep('Realtime SDP answer applied. Waiting for interviewer opening.');
        requestOpeningResponse(sessionPayload, 'remote_description.ready');
      } catch (err) {
        debugError('realtime_start_failed', { message: err?.message || 'unknown error' });
        fail(localizedRuntimeError(err, 'startRealtimeFailed'));
      }
    });
  };

  const startLiveKitInterview = async (sessionPayload) => {
    if (!sessionPayload.sessionId) throw new Error(operational('missingSession'));
    if (!sessionPayload.livekitUrl || !sessionPayload.participantToken) {
      throw new Error(operational('missingVoiceRoom'));
    }

    const room = new Room({
      adaptiveStream: true,
      disconnectOnPageLeave: true,
    });
    liveKitRoomRef.current = room;
    liveKitOpeningCompleteRef.current = false;
    const remoteAudio = new Audio();
    remoteAudio.autoplay = true;
    remoteAudio.playsInline = true;
    audioRef.current = remoteAudio;
    let remoteAudioTrack = null;
    let playbackPromise = null;
    let agentReady = false;
    let resolveAgentReady;
    let rejectAgentReady;
    const agentReadyPromise = new Promise((resolve, reject) => {
      resolveAgentReady = resolve;
      rejectAgentReady = reject;
    });
    const markAgentReady = () => {
      if (agentReady) return;
      agentReady = true;
      markLiveStarted();
      resolveAgentReady();
    };
    const startRemoteAudio = () => {
      if (!remoteAudioTrack || agentReady || playbackPromise) return playbackPromise;
      playbackPromise = (async () => {
        remoteAudioTrack.attach(remoteAudio);
        await room.startAudio();
        await remoteAudio.play();
        markAgentReady();
        recordStartupStep('Server interviewer audio is playing.');
      })().catch((err) => {
        rejectAgentReady(new Error(operational('browserAudioFailed')));
        throw err;
      });
      return playbackPromise;
    };

    room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
      const text = await reader.readAll();
      const attributes = reader.info?.attributes || {};
      if (attributes['lk.transcription_final'] !== 'true') return;
      const role = participantInfo.identity === room.localParticipant.identity
        ? 'candidate'
        : 'interviewer';
      addTranscript(role, text);
    });

    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== Track.Kind.Audio) return;
      remoteAudioTrack = track;
      startRemoteAudio()?.catch((err) => {
        debugError('livekit_audio_playback_failed', { message: err?.message || 'unknown error' });
      });
      recordStartupStep('Server interviewer audio track connected.');
    });
    room.on(RoomEvent.ParticipantAttributesChanged, (changedAttributes) => {
      if (changedAttributes['jobseeqr.interview.complete'] === 'true') {
        endInterview(false, null, {
          endedByAgent: true,
          completionReason: 'completed_by_interviewer',
        });
        return;
      }
      if (
        changedAttributes['jobseeqr.interview.opening_complete'] === 'true'
        && !liveKitOpeningCompleteRef.current
      ) {
        liveKitOpeningCompleteRef.current = true;
        setMicrophoneEnabled(true);
        setMode('listening');
        recordStartupStep('Opening finished. Candidate microphone enabled.');
      }
      const agentState = changedAttributes['lk.agent.state'];
      if (!agentState) return;
      if (agentState === 'speaking') {
        setMode('speaking');
      } else if (agentState === 'thinking') {
        setMode('thinking');
      } else if (agentState === 'listening') {
        setMode('listening');
      }
    });
    room.on(RoomEvent.Disconnected, () => {
      if (!endingRef.current && sessionInfoRef.current) {
        debugLog('livekit_room_disconnected', { sessionId: sessionPayload.sessionId });
        if (!agentReady) {
          rejectAgentReady(new Error(operational('disconnected')));
        } else {
          endInterview(true, null, {
            completionReason: 'voice_connection_lost',
          });
        }
      }
    });

    recordStartupStep('Connecting secure WebRTC interview room.');
    const microphoneTrack = await createLocalAudioTrack({
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    });
    await microphoneTrack.mute();
    liveKitMicrophoneTrackRef.current = microphoneTrack;
    await room.connect(sessionPayload.livekitUrl, sessionPayload.participantToken, {
      autoSubscribe: true,
    });
    await room.localParticipant.publishTrack(microphoneTrack, {
      source: Track.Source.Microphone,
    });
    await room.startAudio();
    startRemoteAudio()?.catch((err) => {
      debugError('livekit_audio_playback_failed', { message: err?.message || 'unknown error' });
    });
    recordStartupStep('Microphone held muted until the interviewer finishes the opening.');
    let startupTimeoutId;
    try {
      await Promise.race([
        agentReadyPromise,
        new Promise((_, reject) => {
          startupTimeoutId = window.setTimeout(
            () => reject(new Error(operational('noAudio'))),
            STARTUP_TIMEOUT_MS,
          );
        }),
      ]);
    } finally {
      window.clearTimeout(startupTimeoutId);
    }
  };

  const speakDemoPrompt = (text) => {
    window.speechSynthesis?.cancel?.();
    const utterance = new SpeechSynthesisUtterance(text);
    const requestedLanguage = language === 'ar' ? 'ar-SA' : 'en-US';
    const availableVoices = window.speechSynthesis?.getVoices?.() || [];
    const preferredVoice = availableVoices.find((voice) => (
      String(voice?.lang || '').toLowerCase().startsWith(language === 'ar' ? 'ar' : 'en')
    ));
    utterance.lang = preferredVoice?.lang || requestedLanguage;
    if (preferredVoice) utterance.voice = preferredVoice;
    utterance.rate = 0.96;
    utterance.pitch = 0.92;
    utterance.onstart = () => setMode('speaking');
    utterance.onend = () => setMode('listening');
    window.speechSynthesis?.speak?.(utterance);
  };

  const startInterview = async () => {
    if (startPendingRef.current || status !== 'setup') return;
    if (!selectedOpportunityId) {
      setError(operational('chooseOpportunity'));
      return;
    }
    if (!hasUploadedCv) {
      setError(operational('uploadCvRequired'));
      return;
    }
    if (!paid) {
      setShowUpgrade(true);
      return;
    }
    const chosenTypeMeta = interviewTypeFor('structured');
    activeInterviewTypeRef.current = 'structured';
    setActiveInterviewType('structured');
    debugLog('session_start_clicked', { interviewType: 'structured', jobId: selectedOpportunityId });
    setStartupSteps([]);
    recordStartupStep('Start clicked.', { interviewType: 'structured', jobId: selectedOpportunityId });
    startPendingRef.current = true;
    setError('');
    setPreparingMessage(operational('creatingSession'));
    setReportRecord(null);
    transcriptRef.current = [];
    setTranscript([]);
    setElapsed(0);
    setCandidateSpeechState(false);
    interviewerQuestionCountRef.current = 0;
    setInterviewerQuestionCount(0);
    startedAtRef.current = null;
    elapsedRef.current = 0;
    setStatus('connecting');
    let createdSession = null;

    try {
      clearUpgradeDraft(user?.uid, 'interview');
      const sessionPayload = await createMockInterviewSession({ user, jobId: selectedOpportunityId });
      createdSession = sessionPayload;
      sessionPayload.interviewType = sessionPayload.interviewType || 'structured';
      recordStartupStep('Backend session payload received.', {
        sessionId: sessionPayload.sessionId,
        configured: sessionPayload.configured,
      });
      debugLog('session_payload_received', {
        sessionId: sessionPayload.sessionId,
        provider: sessionPayload.provider,
        configured: sessionPayload.configured,
        contextStatus: sessionPayload.contextStatus,
        monthlyRemaining: sessionPayload.monthlyRemaining,
      });
      setPreparingMessage(operational('preparingMaterials'));
      sessionInfoRef.current = sessionPayload;
      setSessionInfo(sessionPayload);
      const nextDemoPrompts = buildDemoPrompts(sessionPayload, chosenTypeMeta);

      if (sessionPayload.configured) {
        if (sessionPayload.provider === 'livekit-chained') {
          await startLiveKitInterview(sessionPayload);
        } else {
          await startRealtimeInterview(sessionPayload);
        }
      } else {
        startedAtRef.current = Date.now();
        elapsedRef.current = 0;
        setElapsed(0);
        setStatus('live');
        addTranscript('interviewer', nextDemoPrompts[0]);
        speakDemoPrompt(nextDemoPrompts[0]);
      }
    } catch (err) {
      debugError('session_start_failed', { message: err.message });
      cleanupRealtime();
      if (createdSession?.sessionId && !startedAtRef.current) {
        try {
          await cancelMockInterviewSession({
            user,
            sessionId: createdSession.sessionId,
            reason: 'voice_session_failed_to_start',
          });
        } catch (cancelError) {
          debugError('session_cancel_failed', { message: cancelError?.message || 'unknown error' });
        }
      }
      sessionInfoRef.current = null;
      setSessionInfo(null);
      setStatus('setup');
      setMode('idle');
      setActiveInterviewType(null);
      const startErrorKey = ['NotAllowedError', 'SecurityError'].includes(err?.name)
        ? 'microphonePermission'
        : ['NotFoundError', 'DevicesNotFoundError'].includes(err?.name)
          ? 'microphoneUnavailable'
          : 'startFailed';
      setError(startErrorKey === 'startFailed'
        ? localizedRuntimeError(err, startErrorKey)
        : operational(startErrorKey));
    } finally {
      setPreparingMessage('');
      startPendingRef.current = false;
    }
  };

  const endInterview = async (truncated, transcriptOverride = null, options = {}) => {
    if (endingRef.current) return;
    endingRef.current = true;
    setEnding(true);
    setMode('idle');
    window.speechSynthesis?.cancel?.();
    if (sessionInfoRef.current?.configured !== false) {
      recordStartupStep('Finalizing transcript before report generation.', {
        transcriptItems: transcriptRef.current.length,
      });
      await new Promise((resolve) => window.setTimeout(resolve, options.endedByAgent ? 500 : 1400));
    }
    if (dataChannelRef.current?.readyState === 'open') {
      sendRealtimeEvent({ type: 'response.cancel' });
    }
    cleanupRealtime();

    try {
      const currentSession = sessionInfoRef.current || sessionInfo || {};
      const finalInterviewType = currentSession.interviewType || activeInterviewTypeRef.current || interviewType;
      const finalTranscript = transcriptOverride && transcriptOverride.length ? transcriptOverride : transcriptRef.current;
      const candidateTurns = finalTranscript.filter((item) => item.role === 'candidate');
      if (!candidateTurns.length) {
        if (currentSession.sessionId && !candidateTurns.length) {
          try {
            await cancelMockInterviewSession({
              user,
              sessionId: currentSession.sessionId,
              reason: 'interview_ended_without_candidate_answer',
            });
          } catch (cancelError) {
            debugError('session_cancel_failed', { message: cancelError?.message || 'unknown error' });
          }
        }
        sessionInfoRef.current = null;
        setSessionInfo(null);
        setStatus('setup');
        setError(operational('noCandidateAnswer'));
        return;
      }
      setStatus('reporting');
      debugLog('report_create_requested', {
        transcriptItems: finalTranscript.length,
        candidateTurns: candidateTurns.length,
        endedByAgent: Boolean(options.endedByAgent),
        truncated,
      });
      const report = await createMockInterviewReport({
        user,
        interviewType: finalInterviewType,
        sessionId: currentSession.sessionId || '',
        jobDescription: currentSession.jobDescription || '',
        transcript: finalTranscript,
        durationSeconds: elapsedRef.current || elapsed,
        truncated,
        endedByAgent: Boolean(options.endedByAgent),
        completionReason: options.completionReason || '',
        locale: currentSession.locale || language,
      });
      setReportRecord(report);
      setPreviousReports((items) => [report, ...items.filter((item) => item.id !== report.id && item.createdAt !== report.createdAt)].slice(0, 10));
      setStatus('report');
    } catch (err) {
      setError(localizedRuntimeError(err, 'reportFailed'));
      setStatus('setup');
    } finally {
      endingRef.current = false;
      setEnding(false);
    }
  };

  const viewPreviousReport = (record) => {
    cleanupRealtime();
    window.speechSynthesis?.cancel?.();
    const typeKey = record?.interviewType || interviewType;
    activeInterviewTypeRef.current = typeKey;
    setActiveInterviewType(typeKey);
    sessionInfoRef.current = null;
    setSessionInfo(null);
    setReportRecord(record);
    setError('');
    setMode('idle');
    setStatus('report');
    if (!record?.id) return;
    navigate(`/mock-interview/reports/${encodeURIComponent(record.id)}`);
  };

  const retryReportGeneration = async () => {
    if (!reportRecord?.id || !user || reportRetrying) return;
    setReportRetrying(true);
    setError('');
    try {
      const nextReport = await retryMockInterviewReport({ user, reportId: reportRecord.id });
      setReportRecord(nextReport);
      setPreviousReports((items) => [nextReport, ...items.filter((item) => item.id !== nextReport.id)].slice(0, 10));
    } catch (err) {
      setError(localizedRuntimeError(err, 'retryReportFailed'));
    } finally {
      setReportRetrying(false);
    }
  };

  const returnToSetup = () => {
    setStatus('setup');
    setMode('idle');
    setReportRecord(null);
    sessionInfoRef.current = null;
    setSessionInfo(null);
    setActiveInterviewType(null);
    activeInterviewTypeRef.current = interviewType;
    agentEndingRef.current = false;
    setReportRetrying(false);
  };

  const returnToReports = () => {
    cleanupRealtime();
    window.speechSynthesis?.cancel?.();
    navigate('/mock-interview/reports');
    setStatus('reports');
    setMode('idle');
    setReportRecord(null);
    sessionInfoRef.current = null;
    setSessionInfo(null);
    setActiveInterviewType(null);
    activeInterviewTypeRef.current = interviewType;
    setReportRetrying(false);
  };

  const closeReport = reportsOnly ? returnToReports : returnToSetup;
  const closeReportLabel = reportsOnly ? tm('backReports') : tm('practiceAgain');

  const renderReportPicker = () => (
    <section className="js-mock-reports-page" aria-label={tm('previousReports')}>
      <div className="js-mock-reports-page__intro">
        <h2>{tm('previousReports')}</h2>
        <p>{tm('previousCopy')}</p>
      </div>
      <div className="js-mock-history js-mock-history--page">
        <div className="js-mock-history__head">
          <h3>{tm('savedReports')}</h3>
          <span>{reportsLoading ? tm('loading') : fill(tm('savedCount'), { count: previousReports.length })}</span>
        </div>
        {previousReports.length > 0 ? (
          <div className="js-mock-history__list">
            {previousReports.map((record) => {
              const typeMeta = interviewTypes.find((item) => item.key === record.interviewType) || interviewTypes[0];
              const title = reportTitle(record, tm('reportTitle'));
              const date = formatReportDate(record.createdAt, language, tm('recent'));
              return (
                <button
                  type="button"
                  key={record.id || record.createdAt}
                  className="js-mock-history__item"
                  aria-label={fill(tm('openReport'), { title, date })}
                  onClick={() => viewPreviousReport(record)}
                >
                  <FiFileText />
                  <span>
                    <strong>{title}</strong>
                    <small>{typeMeta.label} · {date} · {reportStatusCopy(record, tm)}</small>
                  </span>
                  <em>{reportGenerationFailed(record) ? tm('retryShort') : scoreLabel(reportScore(record.report), tm('noScore'))}</em>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="js-mock-history__empty-state">
            <p>{reportsLoading ? tm('loadingPrevious') : tm('noPrevious')}</p>
            {!reportsLoading && (
              <button type="button" className="js-mock-primary" onClick={() => navigate('/mock-interview')}>
                <FiPlay /> {tm('startPractice')}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );

  if (!paid && showUpgrade) {
    return (
      <UpgradeGate
        user={user}
        context="interview"
        onBack={() => setShowUpgrade(false)}
        backLabel={language === 'ar' ? 'العودة إلى إعداد المقابلة' : 'Back to Interview Setup'}
        onCheckout={() => {
          saveUpgradeDraft(user?.uid, 'interview', { jobId: selectedOpportunityId });
          rememberUpgradeIntent(user?.uid, { path: '/mock-interview' });
        }}
      />
    );
  }

  return (
    <div className="js-mock-interview">
      <header className="js-mock-interview__top">
        <button
          type="button"
          className="js-mock-interview__back"
          onClick={() => navigate(reportsOnly ? '/mock-interview' : '/career-coach')}
        >
          <FiArrowLeft className="js-icon--directional" /> {reportsOnly ? tm('practice') : tm('careerCoach')}
        </button>
        <div>
          <p>{reportsOnly ? tm('reportsTitle') : tm('mockTitle')}</p>
          <h1>{reportsOnly && screenStatus !== 'report' ? tm('previousTitle') : displayedType.label}</h1>
        </div>
        <div className="js-focus-plan-actions"><span className={`js-mock-interview__status is-${mode}`}>{localizedStatus}</span><PlanControlSlot /></div>
      </header>

      {reportsOnly && screenStatus !== 'report' && renderReportPicker()}

      {(screenStatus === 'setup' || isInitializingInterview) && (
        <section className="js-mock-setup" aria-label={tm('chooseLabel')}>
          <div className="js-mock-setup__copy">
            <h2 className="js-section-title">{tm('chooseTitle')}</h2>
            <p>{tm('chooseCopy')}</p>
            {opportunities.length > 0 && <div className="js-mobile-only js-mock-job-search">
              <label htmlFor="interview-job-search">{language === 'ar' ? 'ابحث عن وظيفة للتدرّب عليها' : 'Find a job to practise for'}</label>
              <div><input id="interview-job-search" type="search" value={jobQuery} disabled={isInitializingInterview} onChange={event => setJobQuery(event.target.value)} placeholder={language === 'ar' ? 'المسمّى الوظيفي أو الشركة' : 'Job title or company'} />
                {jobQuery && <button type="button" onClick={() => setJobQuery('')}>{language === 'ar' ? 'مسح' : 'Clear'}</button>}</div>
              {mobile && jobQuery && selectedOpportunity && !visibleOpportunities.some(item => item.id === selectedOpportunityId) && <p role="status">{language === 'ar' ? 'الوظيفة المختارة: ' : 'Selected: '}{selectedOpportunity.title} · {selectedOpportunity.companyName}</p>}
            </div>}
          </div>
          {!hasUploadedCv && (
            <div className="js-mock-prerequisite">
              <FiFileText aria-hidden="true" />
              <span>
                <strong>{tm('addCv')}</strong>
                <small>{tm('addCvCopy')}</small>
              </span>
              <button type="button" onClick={() => navigate('/profile')}>{tm('uploadCv')}</button>
            </div>
          )}
          {!opportunitiesLoaded ? (
            <div className="js-mock-opportunities__empty" role="status">{tm('loadingMatches')}</div>
          ) : opportunities.length > 0 ? (
            <div className="js-mock-opportunities" role="radiogroup" aria-label={tm('matchGroup')}>
              {visibleOpportunities.length === 0 && <p role="status">{language === 'ar' ? 'لا توجد نتائج. جرّب مسمّى آخر أو امسح البحث.' : 'No jobs found. Try another title or clear your search.'}</p>}
              {visibleOpportunities.map((opportunity) => {
                const isSelected = selectedOpportunityId === opportunity.id;
                return (
                  <article key={opportunity.id} className={`js-mock-opportunity ${isSelected ? 'is-selected' : ''}`}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className="js-mock-opportunity__trigger"
                      onClick={() => {
                        setSelectedOpportunityId(opportunity.id);
                        setError('');
                      }}
                      disabled={isInitializingInterview}
                    >
                      <FiBriefcase aria-hidden="true" />
                      <span className="js-mock-opportunity__copy">
                        <strong>{opportunity.title}</strong>
                        <small>{opportunity.companyName}</small>
                        <em>{[opportunity.location, opportunity.workMode, opportunity.employmentType].filter(Boolean).join(' · ')}</em>
                      </span>
                      <span className="js-mock-opportunity__match">
                        {opportunity.fitScore >= 85 ? tm('excellent') : tm('strong')} {tm('match')}
                        <b>{opportunity.fitScore}%</b>
                      </span>
                      {isSelected && <i className="fa-solid fa-circle-check js-mock-opportunity__check" aria-hidden="true" />}
                    </button>
                    {isSelected && (
                      <div className="js-mock-opportunity__preview" aria-live="polite">
                        <span className="js-mock-opportunity__preview-label">{tm('jobDescription')}</span>
                    {opportunity.description ? (
                      <span className="js-mock-opportunity__preview-description">{opportunity.description}</span>
                    ) : (
                      <span className="js-mock-opportunity__preview-description is-muted">{tm('noJobDescription')}</span>
                    )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="js-mock-opportunities__empty">
              <p>{tm('noMatches')}</p>
              <button type="button" className="js-mock-secondary" onClick={() => navigate('/opportunities')}>{tm('openOpportunities')}</button>
            </div>
          )}
          {sessionInfo?.monthlyLimit ? (
            <p className="js-mock-usage">
              {fill(tm('remaining'), { remaining: sessionInfo.monthlyRemaining, limit: sessionInfo.monthlyLimit })}
            </p>
          ) : (
            <p className="js-mock-usage">{tm('beta')}</p>
          )}
          {error && <p className="js-mock-error">{error}</p>}
          <button
            type="button"
            className="js-mock-primary"
            onClick={startInterview}
            disabled={screenStatus !== 'setup' || !selectedOpportunity || !hasUploadedCv}
          >
            {isInitializingInterview ? <span className="js-mock-button-spinner" aria-hidden="true" /> : <FiPlay />}
            {isInitializingInterview ? tm('preparingInterview') : tm('startInterview')}
          </button>
          <div className="js-mock-setup__actions">
            <button type="button" className="js-mock-secondary" onClick={() => navigate('/mock-interview/reports')} disabled={isInitializingInterview}>
              <FiFileText /> {tm('viewReports')}
              <span>{reportsLoading ? tm('loading') : fill(tm('savedCount'), { count: previousReports.length })}</span>
            </button>
          </div>
        </section>
      )}

      {screenStatus === 'live' && (
        <section className="js-mock-live" aria-label={tm('live')}>
          <div className={`js-voice-orb is-${mode}`}>
            <FiMic />
          </div>
          <p className="js-mock-live__timer">{secondsToClock(elapsed)}</p>
          <div className="js-mock-live__actions">
            <button type="button" className="js-mock-secondary" onClick={() => endInterview(false)} disabled={ending}>
              <FiSquare /> {tm('finish')}
            </button>
            <button type="button" className="js-mock-danger" onClick={() => endInterview(true)} disabled={ending}>
              <FiPhoneOff /> {tm('exitReport')}
            </button>
          </div>
        </section>
      )}

      {screenStatus === 'reporting' && (
        <section className="js-mock-preparing js-mock-reporting" aria-label={tm('submittingLabel')}>
          <div className="js-mock-preparing__pulse" aria-hidden="true">
            <FiFileText />
          </div>
          <h2>{tm('submitting')}</h2>
          <p>{tm('submittingCopy')}</p>
          <button type="button" className="js-mock-secondary" onClick={() => navigate('/career-coach')}>
            {tm('leave')}
          </button>
        </section>
      )}

      {screenStatus === 'report' && (() => {
        if ((reportDetailLoading || (reportsOnly && reportId && !error)) && !reportRecord) {
          return (
            <section className="js-mock-report-loader" aria-label={tm('loadingReport')} aria-live="polite">
              <div className="js-mock-report-loader__spinner" aria-hidden="true" />
              <p>{tm('openingReport')}</p>
            </section>
          );
        }

        if (!reportDetailLoading && !reportRecord && reportsOnly) {
          return (
            <section className="js-mock-report js-mock-report--failed" aria-label={tm('unavailableLabel')}>
              <div className="js-mock-report__head">
                <p>{tm('unavailable')}</p>
                <h2>{tm('couldNotOpen')}</h2>
              </div>
              {error && <p className="js-mock-error">{error}</p>}
              <div className="js-mock-live__actions">
                <button type="button" className="js-mock-secondary" onClick={returnToReports}>{tm('backReports')}</button>
              </div>
            </section>
          );
        }

        const report = reportRecord?.report || {};
        const overall = report.overall || {};
        const score = reportScore(report);
        const failed = reportGenerationFailed(reportRecord);
        const scorecard = asArray(report.scorecard);
        const questionBreakdown = asArray(report.questionBreakdown);
        const communication = report.communicationAnalysis || {};
        const roleAlignment = report.roleAlignment || {};
        const practicePlan = report.practicePlan || {};
        const summary = reportSummary(reportRecord) || error || tm('noContent');

        if (failed) {
          const generation = reportRecord?.reportGeneration || {};
          return (
            <section className="js-mock-report js-mock-report--failed" aria-label={tm('failedLabel')}>
              <div className="js-mock-report__head">
                <p>{reportStatusCopy(reportRecord, tm)}</p>
                <h2>{reportTitle(reportRecord, tm('reportTitle'))}</h2>
              </div>
              <div className="js-mock-report-failure">
                <h3>{tm('fullNotGenerated')}</h3>
                <p>{generation.message || summary}</p>
                <dl>
                  <div>
                    <dt>{tm('reason')}</dt>
                    <dd>{generation.fallbackReason || report.fallbackReason || 'report_generation_failed'}</dd>
                  </div>
                  <div>
                    <dt>{tm('transcriptTurns')}</dt>
                    <dd>{generation.transcriptItems ?? 0}</dd>
                  </div>
                  <div>
                    <dt>{tm('candidateAnswers')}</dt>
                    <dd>{generation.candidateTurns ?? 0}</dd>
                  </div>
                  <div>
                    <dt>{tm('interviewerTurns')}</dt>
                    <dd>{generation.interviewerTurns ?? 0}</dd>
                  </div>
                  <div>
                    <dt>{tm('questionPairs')}</dt>
                    <dd>{generation.questionPairs ?? 0}</dd>
                  </div>
                </dl>
                {generation.failureDetail && (
                  <p className="js-mock-report-failure__detail">{generation.failureDetail}</p>
                )}
                {error && <p className="js-mock-error">{error}</p>}
                <div className="js-mock-live__actions">
                  <button
                    type="button"
                    className="js-mock-primary"
                    onClick={retryReportGeneration}
                    disabled={reportRetrying || generation.retryable === false}
                  >
                    <FiRefreshCw /> {reportRetrying ? tm('retrying') : tm('retry')}
                  </button>
                  <button type="button" className="js-mock-secondary" onClick={closeReport}>{closeReportLabel}</button>
                  {reportsOnly && <button type="button" className="js-mock-secondary" onClick={() => navigate('/mock-interview')}>{tm('practiceAgain')}</button>}
                  <button type="button" className="js-mock-secondary" onClick={() => navigate('/career-coach')}>{tm('done')}</button>
                </div>
              </div>
            </section>
          );
        }

        return (
          <section className="js-mock-report js-mock-report--v3" aria-label={tm('reportLabel')}>
            <div className="js-mock-report__intro">
              <div className="js-mock-report__head js-mock-report__head--v2">
                <div>
                  <p className="js-mock-report__eyebrow">{reportStatusCopy(reportRecord, tm)}</p>
                  <h2>{reportTitle(reportRecord, tm('reportTitle'))}</h2>
                  <small>{overall.readinessLevel || tm('coachingReport')} · {formatReportDate(reportRecord?.createdAt, language, tm('recent'))}</small>
                </div>
                {score !== null && (
                  <span className={`js-mock-report-score js-mock-report-score--${scoreTone(score)}`}>
                    <strong>{scoreLabel(score, tm('noScore'))}</strong>
                    <small>{tm('overallScore')}</small>
                  </span>
                )}
              </div>

              <div className="js-mock-report__summary-block">
                <p className="js-mock-report__eyebrow">{tm('reportSummary')}</p>
                <p className="js-mock-report__summary">{summary}</p>
              </div>

              <div className="js-mock-report-hero">
                <article className="js-mock-report-hero__item js-mock-report-hero__item--strength">
                  <span><FiAward aria-hidden="true" /></span>
                  <div>
                    <strong>{tm('topStrength')}</strong>
                    <p>{overall.topStrength || tm('strengthFallback')}</p>
                  </div>
                </article>
                <article className="js-mock-report-hero__item js-mock-report-hero__item--risk">
                  <span><FiAlertTriangle aria-hidden="true" /></span>
                  <div>
                    <strong>{tm('topRisk')}</strong>
                    <p>{overall.topRisk || tm('riskFallback')}</p>
                  </div>
                </article>
                <article className="js-mock-report-hero__item js-mock-report-hero__item--action">
                  <span><FiZap aria-hidden="true" /></span>
                  <div>
                    <strong>{tm('nextAction')}</strong>
                    <p>{overall.nextBestAction || practicePlan.nextMockInterviewFocus || tm('actionFallback')}</p>
                  </div>
                </article>
              </div>
            </div>

            <nav className="js-mock-report__nav" aria-label={tm('reportSections')}>
              {reportLinks.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className={activeReportSection === link.id ? 'is-active' : ''}
                  onClick={(event) => scrollToReportSection(event, link.id)}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <section id="mock-scorecard" className="js-mock-report-block">
              <header className="js-mock-report-block__head">
                <p className="js-mock-report__eyebrow">{tm('performance')}</p>
                <h3>{tm('scorecard')}</h3>
                <p>{tm('scorecardCopy')}</p>
              </header>
              <div className="js-mock-scorecard">
                {scorecard.map((item) => {
                  const itemScore = reportScore({ score: item.score }) ?? 0;
                  const itemTone = scoreTone(itemScore);
                  return (
                    <article key={item.label} className={`js-mock-score-row js-mock-score-row--${itemTone}`}>
                      <div className="js-mock-score-row__top">
                        <strong>{item.label}</strong>
                        <span>{scoreLabel(itemScore, tm('noScore'))}</span>
                      </div>
                      <div className="js-mock-score-row__bar" aria-hidden="true">
                        <i style={{ width: `${itemScore}%` }} />
                      </div>
                      {cleanItems(item.evidenceQuotes).length > 0 && (
                        <blockquote>{cleanItems(item.evidenceQuotes)[0]}</blockquote>
                      )}
                      <p><b>{tm('whyMatters')}</b> {item.whyItMatters}</p>
                      <p><b>{tm('improveIt')}</b> {item.howToImprove}</p>
                    </article>
                  );
                })}
              </div>
            </section>

            <section id="mock-answers" className="js-mock-report-block">
              <header className="js-mock-report-block__head">
                <p className="js-mock-report__eyebrow">{tm('yourAnswers')}</p>
                <h3>{tm('answerCoaching')}</h3>
                <p>{tm('answerCopy')}</p>
              </header>
              {questionBreakdown.length > 0 && (
                <div className="js-mock-answer-stack">
                  {questionBreakdown.map((question, index) => {
                    const questionScore = reportScore({ score: question.score }) ?? 0;
                    return (
                    <article
                      key={`${question.question}-${index}`}
                      className="js-mock-answer-panel"
                    >
                      <div className="js-mock-answer__prompt">
                        <div className="js-mock-answer__heading">
                          <span className="js-mock-answer__number">{fill(tm('question'), { number: index + 1 })}</span>
                          <em className={`is-${scoreTone(questionScore)}`}>{scoreLabel(questionScore, tm('noScore'))}</em>
                        </div>
                        <p className="js-mock-answer__question">{question.question}</p>
                      </div>
                      {question.candidateAnswerExcerpt && (
                        <div className="js-mock-answer__response">
                          <span>{tm('yourAnswer')}</span>
                          <p className="js-mock-answer__excerpt">{question.candidateAnswerExcerpt}</p>
                        </div>
                      )}
                      {cleanItems(question.directQuotes).length > 0 && (
                        <blockquote>{cleanItems(question.directQuotes)[0]}</blockquote>
                      )}
                      <div className="js-mock-answer__grid">
                        <div className="is-positive">
                          <h4><FiCheckCircle aria-hidden="true" /> {tm('worked')}</h4>
                          <ul>{cleanItems(question.whatWorked).map((entry) => <li key={entry}>{entry}</li>)}</ul>
                        </div>
                        <div className="is-warning">
                          <h4><FiAlertTriangle aria-hidden="true" /> {tm('missing')}</h4>
                          <ul>{cleanItems(question.whatWasMissing).map((entry) => <li key={entry}>{entry}</li>)}</ul>
                        </div>
                      </div>
                      {question.strongerAnswerExample && (
                        <div className="js-mock-answer__rewrite">
                          <h4>{tm('trySaying')}</h4>
                          <p>{question.strongerAnswerExample}</p>
                        </div>
                      )}
                      {question.drill && <p className="js-mock-answer__drill"><b>{tm('drill')}</b> {question.drill}</p>}
                    </article>
                    );
                  })}
                </div>
              )}
            </section>

            <section id="mock-communication" className="js-mock-report-block">
              <header className="js-mock-report-block__head">
                <p className="js-mock-report__eyebrow">{tm('delivery')}</p>
                <h3>{tm('communication')}</h3>
                <p>{tm('communicationCopy')}</p>
              </header>
              <div className="js-mock-analysis-grid">
                {[
                  [tm('concision'), communication.concision],
                  [tm('structure'), communication.structure],
                  [tm('filler'), communication.fillerHedgingRepetition],
                  [tm('length'), communication.answerLengthPatterns],
                  [tm('listening'), communication.listeningFollowUpHandling],
                ].filter(([, value]) => value).map(([label, value]) => (
                  <article key={label}>
                    <strong>{label}</strong>
                    <p>{value}</p>
                  </article>
                ))}
              </div>
              {cleanItems(communication.examples).length > 0 && (
                <ul className="js-mock-report-list">
                  {cleanItems(communication.examples).map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
            </section>

            <section id="mock-alignment" className="js-mock-report-block">
              <header className="js-mock-report-block__head">
                <p className="js-mock-report__eyebrow">{tm('selectedJob')}</p>
                <h3>{tm('roleFit')}</h3>
                <p>{tm('roleCopy')}</p>
              </header>
              <div className="js-mock-answer__grid js-mock-role-signals">
                <div className="is-positive">
                  <h4><FiCheckCircle aria-hidden="true" /> {tm('fitSignals')}</h4>
                  <ul>{cleanItems(roleAlignment.fitSignals).map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
                <div className="is-warning">
                  <h4><FiAlertTriangle aria-hidden="true" /> {tm('missingEvidence')}</h4>
                  <ul>{cleanItems(roleAlignment.missingRoleEvidence).map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
              </div>
              <div className="js-mock-role-notes">
                {roleAlignment.jobDescriptionAlignment && <p><strong>{tm('jobAlignment')}</strong>{roleAlignment.jobDescriptionAlignment}</p>}
                {roleAlignment.compensationLogistics && <p><strong>{tm('practical')}</strong>{roleAlignment.compensationLogistics}</p>}
              </div>
            </section>

            <section id="mock-practice" className="js-mock-report-block">
              <header className="js-mock-report-block__head">
                <p className="js-mock-report__eyebrow">{tm('nextSteps')}</p>
                <h3>{tm('practicePlan')}</h3>
                <p>{tm('practiceCopy')}</p>
              </header>
              <div className="js-mock-practice-grid">
                {asArray(practicePlan.priorityFixes).map((item, index) => (
                  <article key={`${item.focus}-${index}`}>
                    <span>{index + 1}</span>
                    <div>
                      <strong>{item.focus || fill(tm('priority'), { number: index + 1 })}</strong>
                      {item.why && <p>{item.why}</p>}
                      {item.drill && <p><b>{tm('drill')}</b> {item.drill}</p>}
                      {item.trySayingThis && <blockquote>{item.trySayingThis}</blockquote>}
                    </div>
                  </article>
                ))}
              </div>
              <div className="js-mock-plan-columns">
                <div>
                  <h4>{tm('sevenDay')}</h4>
                  <ul>{cleanItems(practicePlan.sevenDayPlan).map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
                <div>
                  <h4>{tm('homework')}</h4>
                  <ul>{cleanItems(practicePlan.answerBankHomework).map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
              </div>
              {practicePlan.nextMockInterviewFocus && (
                <p className="js-mock-next-focus"><FiZap aria-hidden="true" /><span><b>{tm('nextFocus')}</b>{practicePlan.nextMockInterviewFocus}</span></p>
              )}
            </section>

            {scorecard.length === 0 && questionBreakdown.length === 0 && (
              <div className="js-mock-report__sections">
                {asArray(report.sections).map((section) => (
                  <article key={section.title} className="js-mock-report__section">
                    <h3>{section.title}</h3>
                    <ul>
                      {asArray(section.items).map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </article>
                ))}
              </div>
            )}

            {error && <p className="js-mock-error">{error}</p>}
            <div className="js-mock-live__actions">
              <button
                type="button"
                className="js-mock-secondary"
                onClick={() => navigate('/ask-anything', {
                  state: {
                    mockInterviewReportId: reportRecord?.id,
                    prefill: language === 'ar'
                      ? [
                        'ساعدني في تحويل تقرير المقابلة التدريبية هذا إلى خطة تحسين عملية.',
                        `نوع المقابلة: ${displayedType.label}`,
                        score !== null ? `النتيجة: ${scoreLabel(score)}` : '',
                        overall.topStrength ? `أبرز نقطة قوة: ${overall.topStrength}` : '',
                        overall.topRisk ? `أبرز مخاطرة: ${overall.topRisk}` : '',
                        overall.nextBestAction ? `الإجراء التالي: ${overall.nextBestAction}` : '',
                        summary ? `ملخص التقرير: ${summary}` : '',
                      ].filter(Boolean).join('\n')
                      : [
                        'Help me turn this mock interview report into a concrete improvement plan.',
                        `Interview type: ${displayedType.label}`,
                        score !== null ? `Score: ${scoreLabel(score)}` : '',
                        overall.topStrength ? `Top strength: ${overall.topStrength}` : '',
                        overall.topRisk ? `Top risk: ${overall.topRisk}` : '',
                        overall.nextBestAction ? `Next action: ${overall.nextBestAction}` : '',
                        summary ? `Report summary: ${summary}` : '',
                      ].filter(Boolean).join('\n'),
                  },
                })}
              >
                <FiMessageCircle /> {tm('askCoach')}
              </button>
              <button type="button" className="js-mock-secondary" onClick={closeReport}>{closeReportLabel}</button>
              {reportsOnly && <button type="button" className="js-mock-secondary" onClick={() => navigate('/mock-interview')}>{tm('practiceAgain')}</button>}
              <button type="button" className="js-mock-primary" onClick={() => navigate('/career-coach')}>{tm('done')}</button>
            </div>
          </section>
        );
      })()}
    </div>
  );
}

export default MockInterviewScreen;
